import { createContext } from 'react';

import { type CompanyHoliday } from '../Settings/Context';

import { type defaultCompanySettingsType } from './Provider';

import { DEFAULT_PROJECT_STATE } from '@/components/Projects/CreateProjectModal/constants';
import {
  type Project,
  type ProjectCreate,
  type CompanyResource,
  type ManagerCreate,
  type AddressInput,
  type ActivityInput,
} from '@/graphql/generated/graphql';

export type ManagerOption = {
  id: string;
  name?: string | null;
  email?: string | null;
  user?: {
    id: string;
    firstName?: string | null;
    lastName?: string | null;
    email?: string | null;
    imageUrl?: string | null;
  } | null;
};

interface ProjectContextValueType {
  mode: string; // create | edit
  parentProject: Project | undefined;
  project: ProjectCreate;
  setProject: React.Dispatch<React.SetStateAction<ProjectCreate>>;
  loadingProject: boolean;
  managers: (CompanyResource | ManagerOption)[];
  isProjectCodeUnique: (code: string) => boolean;
  isSaving: boolean;
  lastSaved: Date | null;
  handleCreateProject: (options?: { silent?: boolean; onSuccess?: () => void }) => void;
  onProjectDataChange: (key: string, value: string | ManagerCreate[]) => void;
  onAddressesChange: (addresses: AddressInput[]) => void;
  onTagsChange: (selectedTags: string[]) => void;
  onContractChange: (contractIds: string[]) => void;
  onActivitiesChange: (activities: ActivityInput[]) => void;
  onSettingsChange: (newSettings: { name: string; value: string }[]) => void;
  onHolidaysChange: (holidays: CompanyHoliday[]) => void;
  onClientRepChange: (index: number, field: string, value: string) => void;
  onClientRepRemove: (index: number) => void;
  onAddClientRep: () => void;
  onManagerTitleChange: (manager: ManagerCreate, title: string) => void;
  getManagerData: (companyResourceId?: string | null) => CompanyResource | ManagerOption;
  handleResetProject: (onDone?: () => void) => void;
  resetKey: number;
  defaultCompanySettings: defaultCompanySettingsType;
}

export const ProjectContext = createContext<ProjectContextValueType>({
  mode: 'create',
  parentProject: undefined,
  project: { ...DEFAULT_PROJECT_STATE },
  setProject: () => {},
  loadingProject: false,
  managers: [],
  isProjectCodeUnique: () => true,
  isSaving: false,
  lastSaved: null,
  handleCreateProject: () => {},
  onProjectDataChange: () => {},
  onAddressesChange: () => {},
  onTagsChange: () => {},
  onContractChange: () => {},
  onActivitiesChange: () => {},
  onSettingsChange: () => {},
  onHolidaysChange: () => {},
  onClientRepChange: () => {},
  onManagerTitleChange: () => {},
  getManagerData: () => ({}) as CompanyResource,
  onClientRepRemove: () => {},
  onAddClientRep: () => {},
  handleResetProject: () => {},
  resetKey: 0,
  defaultCompanySettings: {} as defaultCompanySettingsType,
});
